<template>
  <div class="pa-4">
    <div class="d-flex justify-end mb-4">
      <v-btn
        v-if="$vuetify.breakpoint.mdAndUp"
        depressed
        dark
        class="primary-button"
        @click="openGenerateDialog"
      >
        <v-icon left small>mdi-plus</v-icon>
        {{ activeKey ? 'Gerar nova chave' : 'Gerar chave' }}
      </v-btn>
      <v-btn
        v-else
        depressed
        dark
        small
        class="primary-button"
        @click="openGenerateDialog"
      >
        <v-icon>mdi-plus</v-icon>
      </v-btn>
    </div>

    <div v-if="loading" class="d-flex justify-center pa-10">
      <v-progress-circular indeterminate color="#0F3565"></v-progress-circular>
    </div>

    <template v-else>
      <!-- Chave ativa -->
      <div v-if="activeKey" class="mb-8">
        <div class="section-label mb-3">CHAVE ATIVA</div>
        <div class="active-key-card">
          <div class="d-flex align-center mb-4">
            <v-chip small dark color="#BBCF20" class="mr-3">Ativa</v-chip>
            <span class="key-name">{{ activeKey.name }}</span>
            <v-spacer></v-spacer>
            <span class="caption grey--text"
              >Criada em {{ formatDate(activeKey.createdAt) }}</span
            >
          </div>

          <div class="key-notice mb-2">
            <v-icon x-small color="#9e9e9e" class="mr-1"
              >mdi-lock-outline</v-icon
            >
            <span
              >A chave completa é exibida apenas no momento da geração. Copie e
              guarde em local seguro.</span
            >
          </div>
          <div class="key-display mb-4">
            <span class="key-prefix-text">{{ activeKey.prefix }}</span>
            <span class="key-mask">••••••••••••••••••••••••••••••••</span>
          </div>

          <v-row dense class="mb-3">
            <v-col cols="12" sm="4">
              <div class="info-label">Expira em</div>
              <div class="info-value">
                {{ formatDate(activeKey.expiresAt) }}
              </div>
            </v-col>
            <v-col cols="12" sm="4">
              <div class="info-label">Último uso</div>
              <div class="info-value" v-if="activeKey.lastUsedAt">
                {{ formatDate(activeKey.lastUsedAt) }}
              </div>
              <div class="info-value grey--text" v-else>Nunca utilizada</div>
            </v-col>
            <v-col cols="12" sm="4" v-if="activeKey.lastUsedIp">
              <div class="info-label">Último IP</div>
              <div class="info-value">{{ activeKey.lastUsedIp }}</div>
            </v-col>
          </v-row>

          <v-divider class="mb-3"></v-divider>

          <v-btn
            depressed
            small
            dark
            class="quaternary-button"
            @click="revokeDialog = true"
          >
            <v-icon left small>mdi-cancel</v-icon>
            Revogar chave
          </v-btn>
        </div>
      </div>

      <!-- Sem chave ativa -->
      <div v-else class="empty-state">
        <v-icon size="52" color="grey lighten-1">mdi-key-off-outline</v-icon>
        <div class="text-subtitle-1 grey--text mt-3">Nenhuma chave ativa</div>
        <div class="body-2 grey--text mt-1">
          Gere uma chave para integrar seus sistemas com a API da Equipacare
          FIX.
        </div>
      </div>

      <!-- Histórico -->
      <template v-if="revokedKeys.length > 0">
        <div class="section-label mb-3">HISTÓRICO</div>
        <div class="history-table">
          <div v-for="key in revokedKeys" :key="key.id" class="history-row">
            <div class="d-flex align-center">
              <v-chip x-small dark color="grey" class="mr-3">Revogada</v-chip>
              <span class="body-2 font-weight-medium mr-4">{{ key.name }}</span>
              <span class="key-prefix-small"
                >{{ key.prefix }}••••••••••••••</span
              >
              <v-spacer></v-spacer>
              <span class="caption grey--text">{{
                formatDate(key.createdAt)
              }}</span>
            </div>
          </div>
        </div>
      </template>
    </template>

    <!-- Dialog: Gerar chave -->
    <v-dialog v-model="generateDialog" max-width="500" persistent>
      <v-card>
        <v-card-title class="dialog-title-add">
          {{ activeKey ? 'Gerar nova chave' : 'Gerar chave de API' }}
        </v-card-title>
        <v-card-text>
          <div v-if="activeKey" class="revoke-warning mb-4">
            <v-icon small color="#e65100" class="revoke-warning-icon"
              >mdi-alert-outline</v-icon
            >
            <span
              >A chave atual será <strong>revogada automaticamente</strong>.
              Integrações usando a chave antiga irão parar de funcionar.</span
            >
          </div>

          <label class="form-label"
            >Nome da chave <span class="required">*</span></label
          >
          <v-text-field
            v-model="form.name"
            placeholder="Ex: Integração ERP, Sistema Externo"
            outlined
            class="primary-input"
            hide-details="auto"
            :error-messages="nameError"
            @input="nameError = ''"
          ></v-text-field>

          <label class="form-label">Validade (dias)</label>
          <v-text-field
            v-model.number="form.expiresInDays"
            type="number"
            min="1"
            max="3650"
            hide-details="auto"
            outlined
            class="primary-input"
          ></v-text-field>
        </v-card-text>
        <v-card-actions class="pb-4 px-6">
          <v-btn depressed class="clear-button" @click="generateDialog = false"
            >Cancelar</v-btn
          >
          <v-spacer></v-spacer>
          <v-btn
            depressed
            dark
            class="primary-button"
            :loading="generating"
            @click="generateKey"
          >
            Gerar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Exibir chave gerada (one-time) -->
    <v-dialog v-model="showKeyDialog" max-width="520" persistent>
      <v-card>
        <v-card-title class="dialog-title-add"
          >Chave gerada com sucesso</v-card-title
        >
        <v-card-text>
          <div class="revoke-warning mb-4">
            <v-icon small color="#e65100" class="revoke-warning-icon"
              >mdi-alert-outline</v-icon
            >
            <span
              ><strong>Copie agora.</strong> Por segurança, esta chave não será
              exibida novamente.</span
            >
          </div>

          <label class="label">Sua chave de API</label>
          <v-text-field
            :value="generatedKey"
            readonly
            outlined
            class="primary-input"
            append-icon="mdi-content-copy"
            @click:append="copyKey"
          ></v-text-field>

          <div v-if="copied" class="d-flex align-center success--text body-2">
            <v-icon small color="success" class="mr-1">mdi-check</v-icon>
            Copiado para a área de transferência!
          </div>
        </v-card-text>
        <v-card-actions class="pb-4 px-6">
          <v-spacer></v-spacer>
          <v-btn
            depressed
            dark
            class="primary-button"
            @click="closeShowKeyDialog"
          >
            Entendi, já copiei
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Confirmar revogação -->
    <v-dialog v-model="revokeDialog" max-width="320">
      <div class="text-center">
        <v-sheet
          class="px-7 pt-7 pb-4 mx-auto text-center d-inline-block"
          color="blue-grey darken-3"
          dark
        >
          <div class="grey--text text--lighten-1 text-body-2 mb-4">
            Tem certeza que deseja revogar a chave
            <strong>{{ activeKey && activeKey.name }}</strong
            >? Esta ação não pode ser desfeita.
          </div>
          <v-btn
            :disabled="revoking"
            class="ma-1"
            color="grey"
            text
            @click="revokeDialog = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            :loading="revoking"
            class="ma-1"
            color="error"
            text
            @click="revokeKey"
          >
            Revogar
          </v-btn>
        </v-sheet>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import ApiService from '../../../../services/ApiService'

const apiService = new ApiService()

export default {
  name: 'ApiKeys',
  data() {
    return {
      loading: false,
      generating: false,
      revoking: false,
      generateDialog: false,
      showKeyDialog: false,
      revokeDialog: false,
      copied: false,
      generatedKey: '',
      nameError: '',
      keys: [],
      form: {
        name: '',
        expiresInDays: 365,
      },
    }
  },
  computed: {
    institutionId() {
      return JSON.parse(localStorage.getItem('institution'))?.id
    },
    activeKey() {
      return this.keys.find((k) => k.status === 0) || null
    },
    revokedKeys() {
      return this.keys.filter((k) => k.status !== 0)
    },
  },
  mounted() {
    this.fetchKeys()
  },
  methods: {
    async fetchKeys() {
      this.loading = true
      try {
        const res = await apiService.get(
          `integration/api-keys?institutionId=${this.institutionId}`
        )
        this.keys = res || []
      } finally {
        this.loading = false
      }
    },
    openGenerateDialog() {
      this.form.name = ''
      this.form.expiresInDays = 365
      this.nameError = ''
      this.generateDialog = true
    },
    async generateKey() {
      if (!this.form.name) {
        this.nameError = 'O nome da chave é obrigatório'
        return
      }
      this.generating = true
      try {
        const res = await apiService.post('integration/api-keys', {
          name: this.form.name,
          institutionId: this.institutionId,
          expiresInDays: this.form.expiresInDays,
        })
        this.generateDialog = false
        this.generatedKey = res.key
        this.copied = false
        this.showKeyDialog = true
        await this.fetchKeys()
      } finally {
        this.generating = false
      }
    },
    closeShowKeyDialog() {
      this.showKeyDialog = false
      this.generatedKey = ''
    },
    async copyKey() {
      await navigator.clipboard.writeText(this.generatedKey)
      this.copied = true
    },
    async revokeKey() {
      this.revoking = true
      try {
        await apiService.post(
          `integration/api-keys/${this.activeKey.id}/revoke`
        )
        this.revokeDialog = false
        await this.fetchKeys()
      } finally {
        this.revoking = false
      }
    },
    formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    },
  },
}
</script>

<style scoped>
.section-label {
  font-size: 0.72rem;
  font-weight: 700;
  color: #9e9e9e;
  letter-spacing: 0.1em;
}

.active-key-card {
  border-left: 4px solid #0f3565;
  background: #f8f9fc;
  border-radius: 0 8px 8px 0;
  padding: 20px 24px;
}

.key-name {
  font-size: 1rem;
  font-weight: 600;
  color: #202020;
}

.key-notice {
  display: flex;
  align-items: center;
  font-size: 0.75rem;
  color: #9e9e9e;
}

.key-display {
  display: flex;
  align-items: center;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 10px 16px;
  font-family: 'Consolas', 'Monaco', monospace;
}

.key-prefix-text {
  color: #0f3565;
  font-weight: 600;
  font-size: 0.95rem;
  letter-spacing: 0.04em;
}

.key-mask {
  color: #b0b0b0;
  font-size: 0.95rem;
  margin-left: 2px;
  letter-spacing: 0.04em;
}

.info-label {
  font-size: 0.75rem;
  color: #9e9e9e;
  margin-bottom: 2px;
}

.info-value {
  font-size: 0.875rem;
  font-weight: 500;
  color: #202020;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 16px;
  text-align: center;
}

.history-table {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
}

.history-row {
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
}

.history-row:last-child {
  border-bottom: none;
}

.key-prefix-small {
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 0.8rem;
  color: #9e9e9e;
}

.dialog-title-add {
  font-size: 1.2rem !important;
  color: #202020 !important;
  font-weight: 700 !important;
}

.label {
  font-weight: 500;
  font-size: 0.85rem;
  color: #424242;
  margin-bottom: 4px;
  display: block;
}

.form-label {
  font-weight: 400;
  font-size: 0.85rem;
  color: #424242;
  margin-bottom: 4px;
  display: block;
}

.required {
  color: #f44336;
  font-weight: 600;
}

.revoke-warning {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: #fff3e0;
  border-left: 3px solid #e65100;
  border-radius: 4px;
  padding: 10px 12px;
  color: #e65100;
}

.revoke-warning-icon {
  margin-top: 1px;
  flex-shrink: 0;
}

.revoke-warning span {
  font-size: 0.875rem;
  line-height: 1.5;
}
</style>
